import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Quagga from '@ericblade/quagga2';

import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import '../css/Attendance.css';
import { fetchMyEvents, validateTicketAndMarkAttendance } from '../../../../utils/api/organiserApi';
import ScannerPanel from '../components/ScannerPanel.jsx';
import AttendanceStats from '../components/AttendanceStats.jsx';
import ScannedTicketsList from '../components/ScannedTicketsList.jsx';
import AttendanceModals from '../components/AttendanceModals.jsx';

const Attendance = () => {
  const navigate = useNavigate();

  const [isScanning, setIsScanning] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [availableDevices, setAvailableDevices] = useState([]);
  const [scanResult, setScanResult] = useState(null);
  const [manualTicketCode, setManualTicketCode] = useState('');
  const [sessionStats, setSessionStats] = useState({
    total: 0,
    successful: 0,
    failed: 0,
  });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [scannedTickets, setScannedTickets] = useState([]);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [scanPopup, setScanPopup] = useState({ show: false, code: '' });
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const scannerInitialized = useRef(false);
  const manualInputRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [isEventSelectionOpen, setIsEventSelectionOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  useEffect(() => {
    const fetchEventsAndShowSelection = async () => {
      try {
        console.log('[Attendance] 📋 Fetching events — API: fetchMyEvents()');
        const allEvents = await fetchMyEvents();
        console.log('[Attendance] ✅ Events loaded:', {
          columns: 'event_id/_id, event_name/name/title, event_date/startdate/startDate/start_date, end_date/enddate/endDate',
          total: allEvents?.length,
        });
        const now = new Date();
        const currentEvents = allEvents.filter((event) => {
          const startDate = new Date(event.event_date || event.startdate || event.startDate || event.start_date);
          const endDate = new Date(event.end_date || event.enddate || event.endDate);

          const isSameDay = startDate.toDateString() === endDate.toDateString();
          if (isSameDay) {
            return now.toDateString() === startDate.toDateString();
          }

          return now >= startDate && now <= endDate;
        });

        setEvents(currentEvents);

        // Always open the selection modal so the user sees either
        // the list of current events or the "no events" message.
        setIsEventSelectionOpen(true);

        if (currentEvents.length === 0) {
          toast.info('No events are currently occurring');
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        toast.error('Failed to fetch events');
      }
    };

    fetchEventsAndShowSelection();
  }, []);

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setIsEventSelectionOpen(false);
    const eventName = event.event_name || event.name || event.title || 'Unnamed Event';
    toast.success(`Selected event: ${eventName}`);
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const getVideoDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');
      setAvailableDevices(videoDevices);

      if (videoDevices.length > 0) {
        setSelectedDeviceId(videoDevices[0].deviceId);
      }
    } catch (error) {
      console.error('Error getting video devices:', error);
      toast.error('Failed to enumerate video devices');
    }
  };

  useEffect(() => {
    getVideoDevices();
  }, []);

  useEffect(() => {
    if (!isScanning) {
      if (scannerInitialized.current) {
        try {
          Quagga.stop();
          Quagga.offDetected();
          scannerInitialized.current = false;
        } catch (error) {
          console.error('Error stopping Quagga:', error);
        }
      }
      return;
    }

    const initScanner = async () => {
      if (!videoRef.current || !canvasRef.current) {
        toast.error('Failed to initialize scanner: Video or canvas element not found');
        setIsScanning(false);
        return;
      }

      if (scannerInitialized.current) {
        try {
          Quagga.stop();
          Quagga.offDetected();
        } catch (error) {
          console.error('Error stopping existing Quagga instance:', error);
        }
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
            deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
          },
          audio: false,
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch((error) => {
            console.warn('Auto-play prevented, trying with muted video:', error);
            videoRef.current.muted = true;
            return videoRef.current.play();
          });
          streamRef.current = stream;
        }

        const config = {
          inputStream: {
            name: 'Live',
            type: 'LiveStream',
            target: videoRef.current,
            constraints: {
              width: { min: 640, ideal: 1280, max: 1920 },
              height: { min: 480, ideal: 720, max: 1080 },
              aspectRatio: { ideal: 16 / 9 },
              facingMode: 'environment',
              deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
            },
            area: {
              top: '20%',
              right: '20%',
              left: '20%',
              bottom: '20%',
            },
          },
          decoder: {
            readers: [
              'code_128_reader',
              'ean_reader',
              'ean_8_reader',
              'code_39_reader',
              'code_39_vin_reader',
              'codabar_reader',
              'upc_reader',
              'upc_e_reader',
              'i2of5_reader',
              '2of5_reader',
              'code_93_reader',
            ],
          },
          locate: true,
          debug: {
            drawBoundingBox: false,
            showFrequency: false,
            drawScanline: false,
            showPattern: false,
          },
        };

        await new Promise((resolve, reject) => {
          Quagga.init(config, (err) => {
            if (err) {
              toast.error(`Scanner initialization failed: ${err.message || 'Unknown error'}`);
              reject(err);
              return;
            }

            Quagga.onDetected((result) => {
              const code = result.codeResult.code;
              handleScanResult(code);
            });

            scannerInitialized.current = true;
            resolve();
          });
        });

        Quagga.start();
      } catch (error) {
        console.error('Error initializing scanner:', error);
        toast.error(`Failed to initialize scanner: ${error.message}`);
        setIsScanning(false);
      }
    };

    initScanner();

    return () => {
      if (scannerInitialized.current) {
        try {
          Quagga.stop();
          Quagga.offDetected();
          scannerInitialized.current = false;
        } catch (error) {
          console.error('Error cleaning up Quagga:', error);
        }
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setIsScanning(false);
    };
  }, [isScanning, selectedDeviceId]);

  const handleManualSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!manualTicketCode.trim()) {
        setScanResult({
          success: false,
          message: 'Please enter a ticket code',
          code: '',
        });
        return;
      }

      handleScanResult(manualTicketCode.trim());
      setManualTicketCode('');
    },
    [manualTicketCode]
  );

  const toggleInputMode = useCallback(() => {
    const newMode = !isManualEntry;
    setIsManualEntry(newMode);

    if (newMode && isScanning) {
      stopScanner();
    }

    if (newMode) {
      setTimeout(() => {
        if (manualInputRef.current) {
          manualInputRef.current.focus();
        }
      }, 100);
    }
  }, [isManualEntry, isScanning]);

  const playBeep = useCallback((success) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = success ? 800 : 400;
      gainNode.gain.value = 0.1;

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
      oscillator.stop(audioCtx.currentTime + 0.5);

      audioCtx.resume();
    } catch (e) {
      console.warn('Audio playback not supported', e);
    }
  }, []);

  async function handleScanResult(ticketCode) {
    if (!ticketCode || ticketCode.trim() === '') {
      return;
    }

    if (!selectedEvent) {
      setScanResult({
        success: false,
        code: ticketCode,
        timestamp: new Date().toISOString(),
        message: 'No event selected. Please select an event first.',
      });
      playBeep(false);
      setTimeout(() => {
        setScanResult(null);
      }, 2000);
      return;
    }

    const eventId = selectedEvent.event_id || selectedEvent._id || selectedEvent.eventid;
    console.log('[Attendance] 🔍 Validating ticket — API: validateTicketAndMarkAttendance()', { ticketCode, eventId });
    try {
      const response = await validateTicketAndMarkAttendance(ticketCode, eventId);
      console.log('[Attendance] 🎫 Validation result:', { ticketCode, success: response.success, message: response.message });

      const result = {
        success: response.success,
        code: ticketCode,
        timestamp: new Date().toISOString(),
        message:
          response.message ||
          (response.success ? 'Ticket validated successfully!' : 'Invalid ticket'),
        ticketData: response.ticketData || null,
      };

      setScanResult(result);

      setSessionStats((prev) => ({
        total: prev.total + 1,
        successful: response.success ? prev.successful + 1 : prev.successful,
        failed: response.success ? prev.failed : prev.failed + 1,
      }));

      if (response.success) {
        setScannedTickets((prev) => [
          {
            id: Date.now(),
            code: ticketCode,
            timestamp: new Date().toISOString(),
            ticketData: response.ticketData,
          },
          ...prev,
        ]);

        setScanPopup({ show: true, code: ticketCode });
        setTimeout(() => {
          setScanPopup({ show: false, code: '' });
        }, 1500);
      }

      if (isManualEntry) {
        setManualTicketCode('');
        manualInputRef.current?.focus();
      }

      playBeep(response.success);

      setTimeout(() => {
        setScanResult(null);
      }, 2000);
    } catch (error) {
      console.error('Error validating ticket:', error);

      const result = {
        success: false,
        code: ticketCode,
        timestamp: new Date().toISOString(),
        message: error.message || 'Error validating ticket. Please try again.',
      };

      setScanResult(result);

      setSessionStats((prev) => ({
        total: prev.total + 1,
        successful: prev.successful,
        failed: prev.failed + 1,
      }));

      playBeep(false);

      setTimeout(() => {
        setScanResult(null);
      }, 2000);
    }
  }

  const startScanner = async () => {
    try {
      setScanResult(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        stream.getTracks().forEach((track) => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        setAvailableDevices(videoDevices);

        if (videoDevices.length === 0) {
          throw new Error('No video input devices found');
        }

        const environmentCamera = videoDevices.find((device) =>
          device.label.toLowerCase().includes('back') ||
          device.label.toLowerCase().includes('environment')
        );

        const selectedCamera = environmentCamera || videoDevices[0];
        setSelectedDeviceId(selectedCamera.deviceId);

        setIsScanning(true);
      } catch (error) {
        console.error('Error accessing camera:', error);
        if (error.name === 'NotAllowedError') {
          toast.error('Please allow camera access to use the scanner');
        } else if (error.name === 'NotFoundError') {
          toast.error('No camera found on this device');
        } else if (error.name === 'NotSupportedError') {
          toast.error('Camera access is not supported in this browser');
        } else {
          toast.error(`Failed to start camera: ${error.message}`);
        }
        setIsScanning(false);
      }
    } catch (error) {
      console.error('Error in startScanner:', error);
      toast.error(`Failed to start scanner: ${error.message}`);
      setIsScanning(false);
    }
  };

  const stopScanner = () => {
    try {
      if (Quagga && typeof Quagga.stop === 'function') {
        Quagga.stop();
        Quagga.offDetected();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setIsScanning(false);
    } catch (error) {
      console.error('Error stopping scanner:', error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement.tagName;
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') {
        return;
      }

      if (e.key === 'Enter' && manualTicketCode.length >= 6) {
        e.preventDefault();
        handleScanResult(manualTicketCode);
        setManualTicketCode('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [manualTicketCode]);

  const handleExport = (format) => {
    // placeholder; existing export behaviour would go here if needed
    setIsExportModalOpen(false);
  };

  return (
    <div className="attendance-container">
      <GlobalHeader
        title="Attendance"
        subtitle="Scan tickets and track event check-ins"
        backTo="/organiser-dash"
        logoutTo="/login"
        onLogout={handleLogout}
      />

      <div className="main-content">
        <div className="card scanner-card">
          <div className="card-header">
            <span>Scanner</span>
            {!isManualEntry && (
              <select
                className="camera-select"
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                disabled={isScanning}
              >
                {availableDevices.map((device, index) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Camera ${index + 1}`}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="card-content">
            <ScannerPanel
              isManualEntry={isManualEntry}
              isScanning={isScanning}
              availableDevices={availableDevices}
              selectedDeviceId={selectedDeviceId}
              onChangeSelectedDevice={setSelectedDeviceId}
              toggleInputMode={toggleInputMode}
              manualTicketCode={manualTicketCode}
              onManualTicketChange={setManualTicketCode}
              onManualSubmit={handleManualSubmit}
              manualInputRef={manualInputRef}
              videoRef={videoRef}
              canvasRef={canvasRef}
              startScanner={startScanner}
              scanResult={scanResult}
            />
          </div>
        </div>

        <div className="right-side">
          <div className="card stats-card">
            <div className="card-header">Session Statistics</div>
            <div className="card-content">
              <AttendanceStats sessionStats={sessionStats} />
            </div>
          </div>

          <div className="card tickets-card">
            <div className="card-header">Scanned Tickets</div>
            <div className="card-content">
              <ScannedTicketsList
                scannedTickets={scannedTickets}
                onOpenExport={() => setIsExportModalOpen(true)}
              />
            </div>
          </div>
        </div>
      </div>

      <AttendanceModals
        isEventSelectionOpen={isEventSelectionOpen}
        events={events}
        onSelectEvent={handleEventSelect}
        onCloseEventSelection={() => setIsEventSelectionOpen(false)}
        onBackWhenNoEvents={handleBackNavigation}
        isExportModalOpen={isExportModalOpen}
        onCloseExportModal={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        scanPopup={scanPopup}
      />
    </div>
  );
};

export default Attendance;
